<template>
  <Dialog
    v-model="dialogValue"
    v-bind="$attrs"
    content-class="sample-upload-dialog"
    :persistent="!!saveInProgress"
    :show-icon="false"
    :disabled="wrongDatatype || !!saveInProgress"
    showActions
    @save="save"
    @cancel="cancel"
  >
    <template #content>
      <FileInput
        :key="fileInputKey"
        :label="t('File input')"
        multiple
        :disabled="!!saveInProgress"
        :error="wrongDatatype"
        @dataType="(i) => (wrongDatatype = i)"
        @add-files="onFilesSelected"
        @invalid-files="showInvalidFilesError"
      />

      <v-data-table
        :headers="headers"
        :items="combinedFiles"
        :row-props="getRowProps"
        :no-data-text="t('No data available')"
        hide-default-footer
        density="compact"
        class="mt-4"
      >

        <template #item.fileName="{ item }">
          <FileTooltip :file="item" v-if="item.fileName.length > 20">
            <template #content>
              <div class="file-name-ellipsis">
                {{ item.fileName }}
              </div>
            </template>
          </FileTooltip>
          <div v-else>
            {{ item.fileName }}
          </div>
        </template>

        <template #item.comment="{ item }">
          <input
            v-model="item.comment"
            class="px-1"
            :placeholder="t('Add comment...')"
            :disabled="item.delete || saveInProgress"
          />
        </template>

        <template #item.updatedAt="{ item }" class="text-no-wrap">
          {{ normalizeDate(item.updatedAt, true) ?? '-' }}
        </template>

        <template #item.actions="{ index }">
          <Button
            icon-btn
            color="black"
            size="small"
            :disabled="saveInProgress"
            @click="toggleDeleteState(index)"
          >
            <v-icon >
              {{ combinedFiles[index].delete ? 'mdi-undo' : 'mdi-delete-outline' }}
            </v-icon>
          </Button>
        </template>

        <template #no-data>
          <div class="text-center py-4">
            {{ t("No Reports Available") }}
          </div>
        </template>
      </v-data-table>

    </template>

    <template #actions v-if="saveInProgress">
      <v-progress-linear indeterminate color="primary" />
    </template>
  </Dialog>

  <ConfirmationDialog
    v-if="pendingReplace"
    v-model="showReplacingFileDialog"
    :title="t('File already exists')"
    :message="t('File {fileName} already exists. Do you want to replace it?', {
      fileName: pendingFileName,
    })"
    @confirm="replaceExistingFile"
    @cancel="cancelReplacingFile"
  />
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import Dialog from '@/components/dialogs/Dialog.vue'
import FileInput from '@/components/input-components/FileInput.vue'
import Button from '@/components/input-components/Button.vue'
import FileTooltip from '@/components/common/FileTooltip.vue'
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue'
import type {Report, ReportManagementPayload} from '@/entities/report'
import { OrderSupportingFile, OrderSupportingFileUI } from '@/entities/order-supporting-file'
import { useI18n } from "vue-i18n"
import NotificationMessageService from "@/services/notificationmessage.service"
import {normalizeDate} from "@/composables/useDateHelper";

defineOptions({ name: "SampleUploadDialog" })

const dialogValue = defineModel<boolean>({ required: true })

const props = withDefaults(defineProps<{
  initialSupportingFiles: OrderSupportingFileUI[]
  saveInProgress?: boolean
}>(), {
  saveInProgress: false
})

const { t } = useI18n()

const headers = computed(() => [
  { title: t("File name"), value: "fileName", sortable: false },
  { title: t("Comment"), value: "comment", sortable: false },
  { title: t("Updated at"), value: "updatedAt", sortable: false, width: "65px", headerProps: { class: "text-no-wrap" } },
  { title: "", value: "actions", sortable: false, width: "60px" },
])

const emit = defineEmits<{
  (e: 'save', payload: ReportManagementPayload): void
  (e: 'cancel'): void
  (e: 'invalid-files', files: File[]): void
  (e: 'update:modelValue', value: boolean): void
}>()

const getFileExtension = (filename: string): string => {
  const lastDot = filename.lastIndexOf('.')
  return lastDot === -1 ? '' : filename.slice(lastDot + 1).toLowerCase()
}

const FILE_PATTERN = /\.(avi|wmv|mpg|mpeg|mp4|mov|mjpeg|bmp|gif|jpg|jpeg|png|tif|tiff|heif|heic|pdf)$/i
const ALLOWED_EXTENSIONS = (() => {
  const match = FILE_PATTERN.source.match(/\.\(([^)]+)\)/i)
  if (!match) return []
  return match[1].split('|').map(ext => ext.toLowerCase())
})()

function getRowProps({ item }: { item: OrderSupportingFileUI }) {
  return {
    class: {
      'row--new': item.isNew,
      'row--deleted': item.delete,
      'row--unsaved': item.file instanceof File,
    }
  }
}

type CurrentFileType = OrderSupportingFile | Report;
const combinedFiles = ref<OrderSupportingFileUI[]>([])
const showReplacingFileDialog = ref(false)
const pendingReplace = ref<{ existing: CurrentFileType } | null>(null);

const pendingFileName = computed(() => {
  const p = pendingReplace.value;
  if (!p) return '';
  const existing = (p as { existing: CurrentFileType }).existing;
  return existing.fileName ?? '';
});

const wrongDatatype = ref(false)
const fileInputKey = ref(0)

watch(
  [() => dialogValue.value, () => props.initialSupportingFiles],
  ([open, initialFiles]) => {
    if (!open) {
      combinedFiles.value = []
      return
    }
    if (!initialFiles || initialFiles.length === 0) {
      combinedFiles.value = []
      return
    }
    combinedFiles.value = initialFiles.map(f => ({
      ...f,
      originalComment: f.comment ?? '',
      isNew: false,
      delete: f.delete ?? false,
      file: undefined,
      fileChanged: false,
    }))
  },
  { immediate: true }
)

function onFilesSelected(files: File[] | FileList | null) {
  const valid: File[] = []
  const invalid: File[] = []
  const filesArray = Array.isArray(files)
    ? files
    : files instanceof FileList
      ? Array.from(files)
      : files ? [files] : []

  filesArray.forEach(f => {
    if (FILE_PATTERN.test(f.name.trim())) {
      valid.push(f)
    } else {
      console.warn(`File rejected: ${f.name}`)
      invalid.push(f)
    }
  })

  if (valid.length) {
    valid.forEach(file => {
      const existing = combinedFiles.value.find(cf => cf.fileName === file.name && !cf.delete)
      if (existing) {
        pendingReplace.value = {
          file,
          comment: undefined,
          existing: existing as OrderSupportingFile
        }
        showReplacingFileDialog.value = true
      } else {
        const newItem: OrderSupportingFileUI = {
          fileName: file.name,
          comment: '',
          delete: false,
          file: file,
          isNew: true,
          originalComment: '',
          updatedAt: new Date().toISOString(),
          fileChanged: false,
        }
        combinedFiles.value.push(newItem)
      }
    })
  }

  if (invalid.length > 0) {
    emit('invalid-files', invalid)
    showInvalidFilesError(invalid)
  }
}

function showInvalidFilesError(files: File[]) {
  if (!files.length) return

  const invalidDetails = files.map(file => {
    const ext = getFileExtension(file.name)
    return `${file.name} (${ext ? '.' + ext : t('without extension')})`
  }).join(', ')

  NotificationMessageService.showError(
    t('unsupported_file_format_explained', {
      files: invalidDetails,
      allowed: ALLOWED_EXTENSIONS.join(', ')
    })
  )
}

function cancelReplacingFile() {
  showReplacingFileDialog.value = false;
  const p = pendingReplace.value;
  if (p) {
    const existing = (p as { existing: CurrentFileType }).existing;
    const fileName = existing.fileName;
    combinedFiles.value = combinedFiles.value.filter(cf => cf.fileName !== fileName || !cf.isNew);
    pendingReplace.value = null;
  }
}

function replaceExistingFile() {
  if (!pendingReplace.value) return
  const { file, existing } = pendingReplace.value

  const target = combinedFiles.value.find(cf => cf.orderFileId === existing.orderFileId || cf.fileName === existing.fileName)
  if (target) {
    target.file = file
    target.fileChanged = true
    target.fileName = file.name
    target.updatedAt = new Date().toISOString()
    if (!target.isNew) target.isNew = false
  } else {
    combinedFiles.value.push({
      fileId: undefined,
      orderFileId: undefined,
      fileName: file.name,
      comment: existing.comment,
      delete: false,
      file: file,
      isNew: true,
      originalComment: existing.comment,
      updatedAt: new Date().toISOString(),
      fileChanged: true,
    })
  }
  cleanupReplaceState()
}

function cleanupReplaceState() {
  showReplacingFileDialog.value = false
  pendingReplace.value = null
}

function toggleDeleteState(index: number) {
  if (combinedFiles.value[index]) {
    combinedFiles.value[index].delete = !combinedFiles.value[index].delete
  }
}

function save() {
  if (props.saveInProgress) return

  const add = combinedFiles.value
    .filter(f => f.isNew && !f.delete && f.file)
    .map(f => ({ comment: f.comment, file: f.file as File }))

  const remove = combinedFiles.value
    .filter(f => !f.isNew && f.delete && f.orderFileId)
    .map(f => ({
      orderId: f.orderId,
      orderFileId: f.orderFileId,
      fileName: f.fileName
    }))

  const update = combinedFiles.value
    .filter(f =>
      !f.isNew &&
      !f.delete &&
      f.orderFileId &&
      (f.fileChanged || (f.originalComment !== undefined && f.comment !== f.originalComment))
    )
    .map(f => ({
      orderFileId: f.orderFileId!,
      comment: f.comment,
      file: f.file
    }))

  emit('save', { add, remove, update } as ReportManagementPayload)
  fileInputKey.value++
}

function cancel() {
  resetDeleteState()
  cancelReplacingFile()
  emit('update:modelValue', false)
  emit('cancel')
}

function resetDeleteState() {
  combinedFiles.value.forEach(f => {
    if (!f.isNew) f.delete = false
  })
}
</script>

<style lang="scss">
.sample-upload-dialog {
  .file-input-wrapper {
    .v-input__prepend {
      position: absolute;
      top: 6.4rem;
    }
    .v-field__input {
      padding-left: .5rem;
    }
  }
  .no-delete-button {
    margin-right: 64px;
  }

  .file-tooltip {
    display: flex;
    gap: 1rem;
  }

  .file-row {
    gap: .5rem;
    min-width: 0;
  }

  .file-name {
    min-width: 12rem;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .file-comment {
    width: 100%;
  }

  .v-chip {
    display: none;
  }

  tr.row--new {
    background-color: rgba(var(--v-theme-green), 0.08);
  }

  tr.row--unsaved {
    border-left: 4px solid rgb(var(--v-theme-blue));
  }

  tr.row--deleted {
    opacity: 0.5;

    .v-btn {
      color: #000 !important;
    }

    td {
      text-decoration: line-through;
      color: grey;
    }
  }

  .file-name-ellipsis {
    max-width: 200px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: block;
  }

  input {
    width: 100%;
    border-bottom: 1px solid rgba(var(--v-theme-black-50), 0.1);
    outline: none;
    &:focus {
      border-bottom: 1px solid var(--v-primary-base);
    }
    &:disabled {
      background: transparent;
      cursor: not-allowed;
    }
  }
}
</style>
